export const OAUTH_CONNECTED_PATH = '/oauth/connected';

const CHANNEL = 'oauth-connect';

/**
 * Whether this document was loaded as the consent landing page.
 *
 * Decided from the URL before React renders, so the landing page can be mounted
 * on its own rather than as a route. The provider stack the routes sit inside
 * gates rendering on a full auth boot — `LaunchDarklyWrapper` holds a
 * full-screen spinner until the user and the current org resolve — which in a
 * consent popup delays the result report behind network round trips and drops it
 * entirely if the user closes the window first.
 *
 * A trailing slash is tolerated: missing the match would boot the whole app into
 * the popup, the failure this page exists to prevent.
 *
 * @param pathname The document's path.
 * @returns True when the consent landing page should be rendered.
 */
export const isConnectLandingPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '') === OAUTH_CONNECTED_PATH;

export interface OAuthConnectResult {
  provider: string;
  connected: boolean;
}

/**
 * Announce a finished consent round-trip to the tab that started it.
 *
 * One transport, deliberately. A BroadcastChannel is same-origin and needs no
 * window handle, so it reaches the originating tab whether or not the popup's
 * `opener` was severed — which makes it a strict superset of what a
 * `postMessage` to the opener could carry. Publishing on both meant the
 * subscriber delivered the same outcome twice, and two toasts for one sign-in,
 * for any caller that did hold an opener; the second route bought nothing to
 * offset that.
 */
export const publishConnectResult = (result: OAuthConnectResult): boolean => {
  try {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage(result);
    channel.close();
    return true;
  } catch {
    // Unsupported or blocked: the connection still persisted, the originating
    // tab just finds out on its next run instead.
    return false;
  }
};

/**
 * Listen for consent completing in a window this tab opened.
 *
 * Only the BroadcastChannel is listened to, matching the single transport
 * `publishConnectResult` uses. A `message` listener alongside it delivered the
 * same outcome a second time whenever the publisher had an opener to post to.
 *
 * @param onResult Called with each result reported.
 * @returns Unsubscribe function.
 */
export const subscribeToConnectResults = (
  onResult: (result: OAuthConnectResult) => void,
): (() => void) => {
  const deliver = (data: unknown) => {
    const { provider, connected } = (data ?? {}) as Partial<OAuthConnectResult>;
    if (typeof provider === 'string' && typeof connected === 'boolean') {
      onResult({ provider, connected });
    }
  };

  let channel: BroadcastChannel | null = null;
  const onChannelMessage = (event: MessageEvent) => deliver(event.data);
  try {
    channel = new BroadcastChannel(CHANNEL);
    channel.addEventListener('message', onChannelMessage);
  } catch {
    channel = null;
  }

  return () => {
    channel?.removeEventListener('message', onChannelMessage);
    channel?.close();
  };
};

export const CARBON_ARC_PROVIDER = 'carbon-arc';

/** Providers whose flag this page will act on, and how to name them. */
export const CONNECT_PROVIDER_LABELS: Record<string, string> = {
  [CARBON_ARC_PROVIDER]: 'Carbon Arc',
};

/**
 * Read what the OAuth callback left on the landing page's query.
 *
 * The callback appends `?<provider_key>=connected|error`, where the key is the
 * provider slug with underscores (`carbon_arc`), so the flag identifies the
 * provider as well as the outcome. Only known providers are accepted: the key
 * is caller-supplied and ends up on screen, so an arbitrary one would let a
 * crafted link put its own text on a Capitol page.
 *
 * @param search The landing page's query string.
 * @returns The provider and outcome, or null when no known provider flag is present.
 */
export const readConnectLanding = (
  search: string,
): OAuthConnectResult | null => {
  for (const [key, value] of new URLSearchParams(search)) {
    if (value !== 'connected' && value !== 'error') continue;
    const provider = key.replace(/_/g, '-');
    if (!(provider in CONNECT_PROVIDER_LABELS)) continue;
    return { provider, connected: value === 'connected' };
  }
  return null;
};
